import { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { apiFetch } from '../lib/apiClient';
import ImagePickerField from './ImagePickerField';

export default function MenuItemForm({ truckId, item, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: item?.name || '',
    description: item?.description || '',
    // The vendor-entered amount is the exact student-facing menu price.
    price: (item?.price ?? item?.vendor_base_price)?.toString() || '',
    photo_url: item?.photo_url || '',
    prep_time_minutes: item?.prep_time_minutes?.toString() || '10',
    category: item?.category || ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }

    setLoading(true);

    try {
      const url = item
        ? `/api/menu/${item.item_id}`
        : `/api/trucks/${truckId}/menu`;

      const response = await apiFetch(url, {
        method: item ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price),
          prep_time_minutes: parseInt(formData.prep_time_minutes)
        }),
      });

      if (response.ok) {
        const savedItem = await response.json();
        onSaved(savedItem);
        toast.success(item ? 'Item updated' : 'Item added');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl w-full max-w-md max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-bold text-lg">{item ? 'Edit Item' : 'Add Menu Item'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input 
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Classic Burger"
              required
              data-testid="menu-item-name"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Input 
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description"
              data-testid="menu-item-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price">Menu price ($) *</Label>
              <Input 
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                placeholder="9.99"
                required
                data-testid="menu-item-price"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Students see this exact price before checkout taxes and fees.
              </p>
            </div>
            <div>
              <Label htmlFor="prep_time">Prep Time (min)</Label>
              <Input 
                id="prep_time"
                type="number"
                min="1"
                value={formData.prep_time_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, prep_time_minutes: e.target.value }))}
                data-testid="menu-item-prep-time"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Input 
              id="category"
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              placeholder="e.g., Burgers, Sides, Drinks"
              data-testid="menu-item-category"
            />
          </div>

          <div>
            <ImagePickerField
              label="Photo"
              value={formData.photo_url}
              onChange={(url) => setFormData((prev) => ({ ...prev, photo_url: url }))}
              testId="menu-item-photo"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading} data-testid="save-menu-item">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {item ? 'Update' : 'Add Item'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
